interface PreviewSimulationParams {
  tierIds: string[];
  currentSubscriberId: string;
  dynamicContentOverrides: string[];
  cookies: {
    popup_closed?: string;
    email_capture_disabled?: string;
  };
  isLoggedIn: boolean;
  isDraft: boolean;
}

interface PreviewSimulationResult {
  isSimulatingNonSubscriber: boolean;
  isPreviewingWithSimulation: boolean;
  isPopupDisabled: boolean;
  isGatedContentDisabled: boolean;
}

/**
 * Hook to compute preview simulation state for posts and post templates.
 *
 * Preview simulation modes:
 * - tierIds includes 'none' → simulating as non-subscriber (hasn't signed up yet)
 * - tierIds includes 'free' or premium IDs → simulating as subscriber
 * - currentSubscriberId set → simulating as specific subscriber
 * - dynamicContentOverrides set → simulating with specific conditions
 *
 * When simulating as non-subscriber, popups and gated content are shown (not disabled).
 */
const usePreviewSimulation = ({
  tierIds,
  currentSubscriberId,
  dynamicContentOverrides,
  cookies,
  isLoggedIn,
  isDraft,
}: PreviewSimulationParams): PreviewSimulationResult => {
  const isSimulatingNonSubscriber = tierIds.includes('none');
  const isPreviewingWithSimulation =
    (tierIds.length > 0 && !isSimulatingNonSubscriber) ||
    currentSubscriberId !== '' ||
    dynamicContentOverrides.length > 0;

  const isPopupDisabled = !isSimulatingNonSubscriber && (Boolean(cookies.popup_closed) || isLoggedIn);
  const isGatedContentDisabled =
    !isSimulatingNonSubscriber &&
    (Boolean(cookies.email_capture_disabled) || isLoggedIn || (isDraft && isPreviewingWithSimulation));

  return {
    isSimulatingNonSubscriber,
    isPreviewingWithSimulation,
    isPopupDisabled,
    isGatedContentDisabled,
  };
};

export default usePreviewSimulation;
